import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { PeriodStatsTable } from "components/tables/PeriodStatsTable"

import { GetSingleUser } from "services/user/type"
import { userService } from "services/user/user.service"

import { Maybe } from "types/common"

import { formatFiatCurrency, parseAndFormatHistoricalUSD,UNAVAILABLE_DASH } from "util/formatter"

import { BalanceCard } from "../cards/BalanceCard"
import { UserProfileHeader } from "../cards/UserProfileHeader"

interface UserPlayerStatisticsProps {
  userResult: Maybe<GetSingleUser>
  setEditBalance: (value: any) => void
  handleToggleExclusion: () => void
  updateKey: number
  handleAddBonusSuccess: () => void
  setOpenAddBonus: (value: boolean) => void
  setOpenSendMessage: (value: boolean) => void
}

export const UserPlayerStatistics = ({
  userResult,
  setEditBalance,
  handleToggleExclusion,
  updateKey,
  handleAddBonusSuccess,
  setOpenAddBonus,
  setOpenSendMessage
}: UserPlayerStatisticsProps) => {
  if (!userResult) return null
  const { t } = useTranslation("translation", { useSuspense: false })
  const [tableResult, setTableResult] = useState<any>(null)
  const [tableLoading, setTableLoading] = useState(false)

  useEffect(() => {
    if (!tableResult && userResult?.id) {
      setTableLoading(true)
      userService
        .getUserStatsTable(userResult.id)
        .then((result) => {
          if (result) setTableResult(result)
        })
        .catch(console.error)
        .finally(() => setTableLoading(false))
    }
  }, [userResult?.id, tableResult])

  // MS-mode: backend reports `null` balance/financial fields with these flags
  // when FS/bonus-service is down. Show "—" instead of a misleading "$0".
  const balanceUnavailable = userResult.balance_unavailable === true
  const financialsUnavailable = userResult.financials_unavailable === true

  const balanceCards = [
    {
      title: t("user.totalBalance"),
      value: balanceUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.balance ?? ""),
      type: "balance" as const,
      real: balanceUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.usd_balance ?? ""),
      bonus: balanceUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.busd_balance ?? ""),
      setEditBalance
    },
    {
      title: "Total Wagered",
      value: financialsUnavailable ? UNAVAILABLE_DASH : parseAndFormatHistoricalUSD(userResult.wager ?? ""),
      type: "wager" as const
    },
    {
      title: t("user.totalDeposits"),
      value: financialsUnavailable ? UNAVAILABLE_DASH : formatFiatCurrency(userResult.total_deposit_amount?.toString()),
      type: "deposits" as const
    },
    {
      title: t("user.totalWithdrawals"),
      value: financialsUnavailable ? UNAVAILABLE_DASH : formatFiatCurrency(userResult.total_withdrawal_amount?.toString()),
      type: "withdrawals" as const
    },
    {
      title: t("user.totalBets"),
      value: financialsUnavailable ? UNAVAILABLE_DASH : (userResult.total_bets ?? 0).toLocaleString(),
      type: "bets" as const
    }
  ]

  return (
    <div className="w-full flex flex-col sm:grid grid-col-1 gap-8">
      <UserProfileHeader
        userResult={userResult}
        handleToggleExclusion={handleToggleExclusion}
        updateKey={updateKey}
        handleAddBonusSuccess={handleAddBonusSuccess}
        setOpenAddBonus={setOpenAddBonus}
        setOpenSendMessage={setOpenSendMessage}
      />
      {(balanceUnavailable || financialsUnavailable) && (
        <div className="rounded-md px-4 py-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30">
          {t("user.dataTemporarilyUnavailable")}
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row">
        {balanceCards.map((card) => (
          <BalanceCard key={card.type} {...card} />
        ))}
      </div>
      {tableLoading ? (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner loading-md" />
        </div>
      ) : (
        tableResult && <PeriodStatsTable data={tableResult} />
      )}
    </div>
  )
}
